import { useState, useEffect } from 'react';
import { navigationDataService, type NavigationItem } from '@/services/navigation-data-service';
import { usePermissionContext } from '@/context/permission-provider';
import { useUser } from '@/context/user-provider';
import { organizationService } from '@/services/organization-service';

/**
 * useNavigationMenu Hook
 *
 * Provides navigation menu data for the current user.
 * This hook handles:
 * - Fetching navigation from the data service
 * - Loading states
 * - Re-fetching when permissions or organization changes
 *
 * Components using this hook are pure consumers - they just render the data.
 *
 * @returns {Object} Navigation menu data and loading state
 * @property {NavigationItem[]} navItems - Array of navigation items to render
 * @property {boolean} isLoading - Whether navigation data is being loaded
 */
export function useNavigationMenu() {
  const [navItems, setNavItems] = useState<NavigationItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const { isAdmin, isInitialized } = usePermissionContext();
  const { activeOrganization } = useUser();

  useEffect(() => {
    // Don't fetch until permissions are initialized
    if (!isInitialized) {
      setIsLoading(true);
      return;
    }

    const fetchNavigation = async () => {
      try {
        setIsLoading(true);

        // Get navigation data from service
        // Service returns pre-filtered data (simulates backend behavior)
        let navigation = await navigationDataService.getNavigationMenu(
          isAdmin || false,
          activeOrganization?.organizationId
        );

        // Fetch invitation data to add badge information
        try {
          const incomingInvitations = await organizationService.getIncomingInvitations();
          const incomingInvitationCount = incomingInvitations.length;

          // Add badge to both the "Organizations" top-level item and the "Invitations" sub-item
          navigation = navigation.map(section => {
            if (section.title === "Organizations") {
              // Add badge to top-level Organizations item
              const updatedSection = {
                ...section,
                badge: {
                  count: incomingInvitationCount,
                  variant: incomingInvitationCount > 0 ? 'destructive' : 'default',
                  showDot: incomingInvitationCount > 0 // Show red dot when there are invitations
                }
              };
              
              // Add badge to the "Invitations" sub-item
              const updatedItems = section.items?.map(item => {
                if (item.title === "Invitations") {
                  return {
                    ...item,
                    badge: {
                      count: incomingInvitationCount,
                      variant: incomingInvitationCount > 0 ? 'destructive' : 'default',
                      showDot: incomingInvitationCount > 0 // Show red dot when there are invitations
                    }
                  };
                }
                return item;
              });
              
              return { ...updatedSection, items: updatedItems };
            }
            return section;
          });
        } catch (invitationError) {
          console.error('Error fetching invitation data for badges:', invitationError);
          // Continue without badges if invitation fetch fails
        }

        setNavItems(navigation);
      } catch (error) {
        console.error('Error fetching navigation:', error);
        // On error, show empty navigation rather than breaking the UI
        setNavItems([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchNavigation();
  }, [isAdmin, isInitialized, activeOrganization?.organizationId]);

  return {
    navItems,
    isLoading,
  };
}
